import CircularProgress from '@mui/material/CircularProgress'
import { SxProps, useTheme } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TablePagination from '@mui/material/TablePagination'
import React from 'react'

import { Button, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import clsx from 'clsx'
import { makeSx } from '../../theme/_utils'
import { generateRandomGuiID } from '../../utils/commonUtils'
import {
    TokTokDocDataTableCell,
    TokTokDocDataTableHeader,
    TokTokDocDataTableItem,
    TokTokDocDataTableProps,
    TokTokDocDataTableStockyAnchor,
} from './_props/TokTokDocDataTableProps'
import useStyles from './_styles/dataTableStyles'
import FlexBox from './flexBox/FlexBox'
import LargeSwitch from './largeSwitch/LargeSwitch'

const stickySxs = makeSx({
    row: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    cell: {
        position: 'sticky',
        background: '#ffffff',
    },
})

const stickySxsAnchor: Record<TokTokDocDataTableStockyAnchor, SxProps> = {
    left: {
        left: 0,
    },
    right: {
        right: 0,
    },
}

const TokTokDocDataTable = <T extends TokTokDocDataTableItem>({
    loading,
    count,
    itemsPerPage,
    page,
    handlePaginationChange,
    handleItemsPerPageChange,
    headers,
    items,
    describeRows,
    handleMoreColumns,
    displayMoreColumns = false,
    onRowClick = undefined,
    clickableRows = true,
}: TokTokDocDataTableProps<T>) => {
    const classes = useStyles()
    const theme = useTheme()

    const large = useMediaQuery(theme.breakpoints.up('sm'))

    const headerCellsSizes = {
        xs: classes.DataTableCellExtraSmall,
        sm: classes.DataTableCellSmall,
        md: classes.DataTableCellMedium,
        lg: classes.DataTableCellLarge,
    }

    const headerOrientations = {
        start: classes.DataTableCellStart,
        center: classes.DataTableCellCenter,
        end: classes.DataTableCellEnd,
    }

    const getCellStickySx = (cell: TokTokDocDataTableHeader | TokTokDocDataTableCell) => {
        if (!cell.sticky) return undefined

        return {
            ...stickySxs.cell,
            ...stickySxsAnchor[cell.stickyAnchor ?? 'left'],
        }
    }

    const renderHeader = (header: TokTokDocDataTableHeader) => {
        if (header.buttonProps) {
            return (
                <Button {...header.buttonProps} color="inherit" className={classes.DataTableCellButton}>
                    <span className={classes.DataTableCellButtonLabel}> {header.label}</span>
                </Button>
            )
        }

        return header.label
    }

    const getOrientationMatch = (cell: TokTokDocDataTableHeader | TokTokDocDataTableCell) =>
        headerOrientations[cell.orientation ?? 'start']

    const renderBodyCell = (cell: TokTokDocDataTableCell, index: number) => {
        if (!handleDisplay(cell)) return null
        const cellStickySx = getCellStickySx(cell)

        return (
            <TableCell
                key={generateRandomGuiID()}
                sx={cellStickySx}
                className={clsx(
                    index === 0 && classes.DataTableCellFirst,
                    classes.DataTableCell,
                    classes.DataTableBodyCell,
                    cell.hasError && classes.DataTableCellError,
                    getOrientationMatch(cell),
                )}
            >
                {renderBodyCellContent(cell)}
            </TableCell>
        )
    }
    const renderHeaderCell = (header: TokTokDocDataTableHeader) => {
        if (!handleDisplay(header)) return null
        const headerStickySx = getCellStickySx(header)

        const cellSizeMatch = headerCellsSizes[header.size ?? 'sm']
        const randomGuiId = generateRandomGuiID()
        const tableCell = (
            <TableCell
                sx={headerStickySx}
                className={clsx(
                    classes.DataTableCell,
                    classes.DataTableHeaderCell,
                    cellSizeMatch,
                    getOrientationMatch(header),
                )}
            >
                {renderHeader(header)}
            </TableCell>
        )
        if (header.tooltip) {
            return (
                <Tooltip key={randomGuiId} placement="top" title={header.tooltip}>
                    {tableCell}
                </Tooltip>
            )
        }

        return <React.Fragment key={randomGuiId}>{tableCell}</React.Fragment>
    }

    const handleDisplay = (header: TokTokDocDataTableHeader | TokTokDocDataTableCell) => {
        if (header.display !== undefined) {
            if (typeof header.display === 'boolean') {
                return header.display
            }
            if (typeof header.display === 'function') {
                return header.display()
            }

            console.warn(`Unhandled header display type ${typeof header.display}`)
            return true
        }

        return true
    }
    if (loading) {
        return (
            <div className={classes.DataTableContainer}>
                <div className={classes.DataTableLoading}>
                    <CircularProgress size={60} />
                    <Typography>Chargement en cours...</Typography>
                </div>
            </div>
        )
    }

    const renderBodyCellContent = (cell: TokTokDocDataTableCell) => {
        if (cell.icon) {
            return (
                <span className={classes.DataTableCellContentWithIcon}>
                    {cell.icon}
                    {cell.item}
                </span>
            )
        }

        return cell.item
    }

    const handleRowClick = (row: T) => {
        if (!onRowClick) return
        onRowClick(row)
    }

    return (
        <div className={classes.DataTableContainer}>
            <div className={classes.DataTableWrapper}>
                <div className={classes.DataTableWrapperScroller}>
                    <Table className={classes.DataTable} aria-label="user table">
                        {headers && (
                            <TableHead className={classes.DataTableHead}>
                                <TableRow>{headers.map((header) => renderHeaderCell(header))}</TableRow>
                            </TableHead>
                        )}
                        {items && describeRows && (
                            <TableBody className={classes.DataTableBody}>
                                {items.map((item, index) => {
                                    const cells = describeRows(item)
                                    const key = item.id || item.objectId || generateRandomGuiID()
                                    return (
                                        <Tooltip
                                            key={key}
                                            placement="bottom"
                                            arrow
                                            hidden={!item?.tooltip}
                                            title={item?.tooltip}
                                            componentsProps={{
                                                tooltip: {
                                                    className: classes.DataTableRowTooltip,
                                                },
                                                arrow: {
                                                    className: classes.DataTableRowTooltipArrow,
                                                },
                                            }}
                                        >
                                            <TableRow
                                                key={`${item.id || item.objectId || generateRandomGuiID()}`}
                                                className={clsx(
                                                    classes.DataTableRow,
                                                    index % 2 && classes.DataTableRowOdd,
                                                    onRowClick && clickableRows && classes.DataTableRowClickable,
                                                    item?.disabled && classes.DataTableCellDisabled,
                                                )}
                                                sx={stickySxs.row}
                                                onClick={() =>
                                                    clickableRows && !item?.disabled ? handleRowClick(item) : undefined
                                                }
                                            >
                                                {cells?.map((cell) => renderBodyCell(cell, index))}
                                            </TableRow>
                                        </Tooltip>
                                    )
                                })}
                            </TableBody>
                        )}
                    </Table>
                </div>
            </div>
            <FlexBox
                alignItems="center"
                sx={{
                    width: '100%',
                    margin: '0.3rem',
                }}
            >
                {handleMoreColumns && (
                    <FlexBox
                        alignItems="center"
                        sx={{
                            width: '100%',
                            marginLeft: '0.5rem',
                        }}
                    >
                        <LargeSwitch
                            onChange={handleMoreColumns}
                            checked={displayMoreColumns}
                            label="Afficher plus de colonnes"
                        />
                    </FlexBox>
                )}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={count ?? 0}
                    rowsPerPage={itemsPerPage}
                    page={page}
                    labelRowsPerPage={large ? "Nombre d'éléments par page" : ''}
                    labelDisplayedRows={({ from, to, count: max }) =>
                        `${from}-${to} sur ${max !== -1 ? max : `plus que ${to}`}`
                    }
                    onPageChange={handlePaginationChange}
                    onRowsPerPageChange={handleItemsPerPageChange}
                    className={classes.DataTablePagination}
                />
            </FlexBox>
        </div>
    )
}

export default TokTokDocDataTable
